import { useEffect, useMemo, useState } from "react";
import ConfigurationService, {
  ProcessedConfig,
  ServerWidgetConfig,
} from "../services/ConfigurationService";
import { fetchPublicWidgetConfig } from "../services/fetchPublicWidgetConfig";
import { WidgetConfig } from "../store/types";
import { isShellDecided } from "../utils/shellReadiness";

export type UseWidgetConfigurationOptions = {
  /**
   * When false, skip GET /api/widget/config and treat shell as already decided
   * from embed config (admin Live preview). Default true.
   */
  loadPublicConfig?: boolean;
};

interface UseWidgetConfigurationReturn {
  processedConfig: ProcessedConfig;
  errors: string[];
  isValid: boolean;
  /**
   * True when mode-sensitive shell may paint and GPS may follow config.
   * Alias history: formerly `remoteConfigReady`.
   */
  shellDecided: boolean;
}

export const useWidgetConfiguration = (
  config: WidgetConfig,
  options: UseWidgetConfigurationOptions = {}
): UseWidgetConfigurationReturn => {
  const loadPublicConfig = options.loadPublicConfig !== false;
  const widgetId = config.widgetId || config.agentId || "";
  const needsPublicConfig = Boolean(widgetId) && loadPublicConfig;

  const [serverConfig, setServerConfig] = useState<ServerWidgetConfig | null>(
    null
  );
  /** Internal: public fetch finished (or never needed). */
  const [remoteSettled, setRemoteSettled] = useState(() => !needsPublicConfig);

  useEffect(() => {
    if (!needsPublicConfig) {
      setServerConfig(null);
      setRemoteSettled(true);
      return;
    }

    const controller = new AbortController();
    setRemoteSettled(false);

    const apiUrl = ConfigurationService.getApiUrl();

    (async () => {
      const wc = await fetchPublicWidgetConfig(apiUrl, widgetId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setServerConfig(wc);
      setRemoteSettled(true);
    })();

    return () => {
      controller.abort();
    };
  }, [widgetId, needsPublicConfig]);

  const shellDecided = isShellDecided(needsPublicConfig, remoteSettled);

  const mergedEmbed = useMemo(
    () => ConfigurationService.mergeServerConfig(config, serverConfig),
    [config, serverConfig]
  );

  const processedConfig = useMemo(() => {
    const processed = ConfigurationService.processConfig(mergedEmbed);
    // GPS clamp until shellDecided: useGeolocation always mounts in the hook tree.
    if (!shellDecided) {
      return { ...processed, gpsEnabled: false };
    }
    return processed;
  }, [mergedEmbed, shellDecided]);

  const errors = useMemo(() => {
    return ConfigurationService.validateConfig(mergedEmbed);
  }, [mergedEmbed]);

  const isValid = errors.length === 0;

  if (processedConfig.debug && shellDecided) {
    console.log("[useWidgetConfiguration] Processed config:", {
      apiUrl: processedConfig.apiUrl,
      widgetId: processedConfig.widgetId,
      mode: processedConfig.mode,
      uiVariant: processedConfig.uiVariant,
      gpsEnabled: processedConfig.gpsEnabled,
    });
  }

  return {
    processedConfig,
    errors,
    isValid,
    shellDecided,
  };
};
